/**
 * Testimonials Page
 * -----------------
 * Layout:
 *   1. Page header with social proof stats
 *   2. Highlighted featured quote (large format)
 *   3. Testimonial masonry grid — all testimonials
 *   4. Closing CTA
 */
import AnimatedSection from '../../components/AnimatedSection/AnimatedSection';
import TestimonialCard from '../../components/TestimonialCard/TestimonialCard';
import { testimonials } from '../../data/testimonials';
import { Link } from 'react-router-dom';
import styles from './Testimonials.module.css';

/* Platform social-proof numbers */
const proofStats = [
  { value: '2,847', label: 'Researchers trust Labora' },
  { value: '4.9/5',  label: 'Average community rating' },
  { value: '400+',  label: 'Cross-institution collaborations' },
  { value: '52',    label: 'Countries represented' },
];

/* Pull out the first testimonial as a hero quote */
const heroTestimonial  = testimonials[0];
const gridTestimonials = testimonials.slice(1);

const Testimonials = () => (
  <main className={styles.main}>

    {/* ══════════════════════════════════════
        1. PAGE HEADER
    ══════════════════════════════════════ */}
    <section className={styles.pageHeader}>
      <div className={styles.headerGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.headerContent}>
          <span className={styles.breadcrumb}>Community voices</span>
          <h1 className={styles.pageTitle}>
            What scientists say about doing research in the open
          </h1>
          <p className={styles.pageIntro}>
            These are real people doing real science — publicly. They agreed to share their experience in their own words, unedited.
          </p>

          {/* Proof stats */}
          <div className={styles.proofStats}>
            {proofStats.map(({ value, label }) => (
              <div key={label} className={styles.proofStat}>
                <span className={styles.proofValue}>{value}</span>
                <span className={styles.proofLabel}>{label}</span>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        2. FEATURED HERO QUOTE
    ══════════════════════════════════════ */}
    <section className={styles.heroQuoteSection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.heroQuoteCard}>
          {/* Large decorative quote mark */}
          <div className={styles.heroQuoteMark} aria-hidden="true">"</div>

          <blockquote className={styles.heroQuoteText}>
            {heroTestimonial.quote}
          </blockquote>

          <div className={styles.heroQuoteAuthor}>
            <div
              className={styles.heroAvatar}
              style={{
                background: `${heroTestimonial.color}20`,
                color: heroTestimonial.color,
              }}
            >
              {heroTestimonial.initials}
            </div>
            <div>
              <div className={styles.heroAuthorName}>{heroTestimonial.author}</div>
              <div className={styles.heroAuthorField}>{heroTestimonial.field}</div>
              <div className={styles.heroAuthorInstitution}>{heroTestimonial.institution}</div>
            </div>
          </div>

          {/* Stars */}
          <div className={styles.heroStars} aria-label="5 out of 5 stars">
            {[...Array(5)].map((_, i) => (
              <svg key={i} viewBox="0 0 16 16" fill="currentColor" className={styles.star}>
                <path d="M8 1l1.854 3.756L14 5.528l-3 2.924.708 4.129L8 10.396l-3.708 2.185L5 8.452 2 5.528l4.146-.772z"/>
              </svg>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>

    {/* ══════════════════════════════════════
        3. TESTIMONIALS GRID
    ══════════════════════════════════════ */}
    <section className={styles.gridSection}>
      <div className={styles.container}>
        <AnimatedSection className={styles.gridHeader}>
          <h2 className={styles.gridTitle}>More from the community</h2>
          <p className={styles.gridSub}>
            From PhD students to independent researchers, across six continents.
          </p>
        </AnimatedSection>

        {/* CSS columns for masonry effect */}
        <div className={styles.masonryGrid}>
          {gridTestimonials.map((t, i) => (
            <AnimatedSection key={t.id} delay={Math.min(i * 60, 300)} className={styles.masonryItem}>
              <TestimonialCard testimonial={t} />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>

    {/* ══════════════════════════════════════
        4. CLOSING CTA
    ══════════════════════════════════════ */}
    <section className={styles.ctaSection}>
      <div className={styles.ctaGlow} aria-hidden="true" />
      <div className={styles.container}>
        <AnimatedSection className={styles.ctaInner}>
          <span className={styles.ctaEyebrow}>Join the community</span>
          <h2 className={styles.ctaTitle}>
            Your research story<br />belongs here too.
          </h2>
          <p className={styles.ctaSub}>
            Every scientist on this page started by opening one project and posting one update. That's the whole barrier to entry.
          </p>
          <div className={styles.ctaButtons}>
            <Link to="/portfolio" className={styles.ctaPrimary}>
              Start documenting
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M4 10h12M10 4l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <Link to="/portfolio" className={styles.ctaSecondary}>
              Explore projects first
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </section>

  </main>
);

export default Testimonials;
